import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Users, MapPin, Globe, Loader2, User, X, Twitter, Instagram, Linkedin, Youtube, Video, MessageSquare } from "lucide-react";
import { MessageDialog } from "./Messages";
import { useAuth } from "../hooks/useAuth";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to format social handle to URL
//
// Values arrive in whatever shape a member typed: "handle", "@handle", a full
// profile URL, or an "@" with a URL pasted after it. Handles routinely contain
// dots (@spydr.tv, @danny.schaefer), so a dot is NOT evidence of a URL — only a
// scheme or a domain-then-slash is. Getting that wrong sent members to
// unrelated websites.
const SOCIAL_PLATFORMS = {
  twitter:   { host: 'twitter.com',   path: (h) => h },
  instagram: { host: 'instagram.com', path: (h) => h },
  linkedin:  { host: 'linkedin.com',  path: (h) => `in/${h}` },
  youtube:   { host: 'youtube.com',   path: (h) => `@${h}` },
  vimeo:     { host: 'vimeo.com',     path: (h) => h },
};

const formatSocialUrl = (platform, value) => {
  if (!value) return null;

  // Strip leading @ first: members paste both "@handle" and "@" + a full URL.
  const trimmed = String(value).trim().replace(/^@+/, '');
  if (!trimmed) return null;

  // A URL needs a scheme, or a domain followed by a path. A bare handle never
  // contains a slash, which is what separates "spydr.tv" from "x.com/spydr".
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)+\//i.test(trimmed)) return `https://${trimmed}`;

  // Otherwise treat it as a handle; tidy stray slashes and trailing dots.
  const handle = trimmed.replace(/^\/+/, '').replace(/[/.]+$/, '');
  if (!handle) return null;

  const config = SOCIAL_PLATFORMS[platform];
  return config ? `https://${config.host}/${config.path(handle)}` : `https://${handle}`;
};

// Helper to format website URLs
const formatUrl = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `https://${url}`;
};

export default function Members() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [messageRecipient, setMessageRecipient] = useState(null);
  const [onlineUserIds, setOnlineUserIds] = useState([]);
  const [onlineCount, setOnlineCount] = useState(0);

  useEffect(() => {
    fetchMembers();
    fetchOnlineStatus();
    
    // Refresh online status every 30 seconds
    const interval = setInterval(fetchOnlineStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchOnlineStatus = async () => {
    try {
      const [countRes, listRes] = await Promise.all([
        axios.get(`${API}/members/online/count`),
        axios.get(`${API}/members/online/list`)
      ]);
      setOnlineCount(countRes.data.online_count);
      setOnlineUserIds(listRes.data.online_user_ids);
    } catch (error) {
      console.error("Error fetching online status:", error);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/members`);
      setMembers(response.data.members || response.data);
      setTotalCount(response.data.total_count || response.data.length);
    } catch (error) {
      console.error("Error fetching members:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = members.filter((member) =>
    member.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.bio?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.skills?.some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-purple mb-4 font-mono">
            Community
          </p>
          <h1 className="font-syne text-4xl md:text-6xl font-bold text-white mb-6">
            Member Directory
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl">
            Connect with fellow AI filmmakers, artists, and enthusiasts in our community.
          </p>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <input
            type="text"
            placeholder="Search members by name, location, or skills..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="member-search"
            className="w-full max-w-md px-4 py-3 bg-void-surface border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-purple/50"
          />
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="mb-12 flex items-center gap-4 text-slate-500"
        >
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            <span>{totalCount} members</span>
          </div>
          {onlineCount > 0 && (
            <div className="flex items-center gap-2 text-green-400">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              <span>{onlineCount} online now</span>
            </div>
          )}
        </motion.div>

        {/* Members Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-purple animate-spin" />
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="text-center py-20">
            <Users className="w-12 h-12 text-slate-700 mx-auto mb-4" />
            <p className="text-slate-500">
              {searchQuery ? "No members found matching your search." : "No members yet."}
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMembers.map((member, index) => (
              <motion.div
                key={member.user_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                data-testid={`member-${index}`}
                onClick={() => setSelectedMember(member)}
                className="p-6 bg-void-surface border border-white/5 hover:border-purple/20 transition-all duration-500 cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className="relative">
                    <div className="w-14 h-14 rounded-full bg-purple/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {member.picture ? (
                        <img 
                          src={member.picture} 
                          alt={member.name} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-6 h-6 text-purple-light" />
                      )}
                    </div>
                    {/* Online indicator */}
                    {onlineUserIds.includes(member.user_id) && (
                      <span 
                        className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-void-surface rounded-full"
                        title="Online now"
                      ></span>
                    )}
                  </div>
                  <div className="flex-grow min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-syne font-bold text-white truncate">
                        {member.name}
                      </h3>
                      {onlineUserIds.includes(member.user_id) && (
                        <span className="text-xs text-green-400">online</span>
                      )}
                    </div>
                    {member.location && (
                      <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" />
                        {member.location}
                      </p>
                    )}
                  </div>
                </div>

                {member.bio && (
                  <p className="text-sm text-slate-400 mt-4 line-clamp-2">
                    {member.bio}
                  </p>
                )}

                {member.skills && member.skills.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {member.skills.slice(0, 3).map((skill, i) => (
                      <span 
                        key={i}
                        className="px-2 py-1 text-xs bg-white/5 text-slate-400"
                      >
                        {skill}
                      </span>
                    ))}
                    {member.skills.length > 3 && (
                      <span className="text-xs text-slate-500">
                        +{member.skills.length - 3} more
                      </span>
                    )}
                  </div>
                )}

                {/* Social Links and Website */}
                <div className="flex items-center gap-3 mt-4">
                  {member.website && (
                    <a
                      href={formatUrl(member.website)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-slate-500 hover:text-purple-light transition-colors"
                      title="Website"
                    >
                      <Globe className="w-4 h-4" />
                    </a>
                  )}
                  {member.social_links?.twitter && (
                    <a
                      href={formatSocialUrl('twitter', member.social_links.twitter)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-slate-500 hover:text-purple-light transition-colors"
                      title="Twitter/X"
                    >
                      <Twitter className="w-4 h-4" />
                    </a>
                  )}
                  {member.social_links?.instagram && (
                    <a
                      href={formatSocialUrl('instagram', member.social_links.instagram)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-slate-500 hover:text-purple-light transition-colors"
                      title="Instagram"
                    >
                      <Instagram className="w-4 h-4" />
                    </a>
                  )}
                  {member.social_links?.linkedin && (
                    <a
                      href={formatSocialUrl('linkedin', member.social_links.linkedin)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-slate-500 hover:text-purple-light transition-colors"
                      title="LinkedIn"
                    >
                      <Linkedin className="w-4 h-4" />
                    </a>
                  )}
                  {member.social_links?.youtube && (
                    <a
                      href={formatSocialUrl('youtube', member.social_links.youtube)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-slate-500 hover:text-purple-light transition-colors"
                      title="YouTube"
                    >
                      <Youtube className="w-4 h-4" />
                    </a>
                  )}
                  {member.social_links?.vimeo && (
                    <a
                      href={formatSocialUrl('vimeo', member.social_links.vimeo)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-slate-500 hover:text-purple-light transition-colors"
                      title="Vimeo"
                    >
                      <Video className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Member Detail Modal */}
        <AnimatePresence>
          {selectedMember && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
              onClick={() => setSelectedMember(null)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-void-surface border border-white/10 p-8 max-w-lg w-full max-h-[80vh] overflow-y-auto"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className="w-16 h-16 rounded-full bg-purple/20 flex items-center justify-center overflow-hidden">
                        {selectedMember.picture ? (
                          <img 
                            src={selectedMember.picture} 
                            alt={selectedMember.name} 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <User className="w-8 h-8 text-purple-light" />
                        )}
                      </div>
                      {/* Online indicator */}
                      {onlineUserIds.includes(selectedMember.user_id) && (
                        <span 
                          className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-2 border-void-surface rounded-full"
                          title="Online now"
                        ></span>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="font-syne font-bold text-white text-xl">
                          {selectedMember.name}
                        </h2>
                        {onlineUserIds.includes(selectedMember.user_id) && (
                          <span className="px-2 py-0.5 text-xs bg-green-500/20 text-green-400 rounded">Online</span>
                        )}
                      </div>
                      {selectedMember.location && (
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {selectedMember.location}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedMember(null)}
                    className="text-slate-400 hover:text-white transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {selectedMember.bio && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-slate-400 mb-2">About</h3>
                    <p className="text-white">{selectedMember.bio}</p>
                  </div>
                )}

                {selectedMember.skills && selectedMember.skills.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-slate-400 mb-2">Skills</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedMember.skills.map((skill, i) => (
                        <span 
                          key={i}
                          className="px-3 py-1 text-sm bg-purple/20 text-purple-light"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Social Links */}
                <div className="flex items-center gap-4 flex-wrap">
                  {selectedMember.website && (
                    <a
                      href={formatUrl(selectedMember.website)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-purple-light hover:text-white transition-colors"
                    >
                      <Globe className="w-4 h-4" />
                      Website
                    </a>
                  )}
                  {selectedMember.social_links?.twitter && (
                    <a
                      href={formatSocialUrl('twitter', selectedMember.social_links.twitter)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-purple-light hover:text-white transition-colors"
                    >
                      <Twitter className="w-4 h-4" />
                      Twitter
                    </a>
                  )}
                  {selectedMember.social_links?.instagram && (
                    <a
                      href={formatSocialUrl('instagram', selectedMember.social_links.instagram)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-purple-light hover:text-white transition-colors"
                    >
                      <Instagram className="w-4 h-4" />
                      Instagram
                    </a>
                  )}
                  {selectedMember.social_links?.linkedin && (
                    <a
                      href={formatSocialUrl('linkedin', selectedMember.social_links.linkedin)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-purple-light hover:text-white transition-colors"
                    >
                      <Linkedin className="w-4 h-4" />
                      LinkedIn
                    </a>
                  )}
                  {selectedMember.social_links?.youtube && (
                    <a
                      href={formatSocialUrl('youtube', selectedMember.social_links.youtube)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-purple-light hover:text-white transition-colors"
                    >
                      <Youtube className="w-4 h-4" />
                      YouTube
                    </a>
                  )}
                  {selectedMember.social_links?.vimeo && (
                    <a
                      href={formatSocialUrl('vimeo', selectedMember.social_links.vimeo)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-purple-light hover:text-white transition-colors"
                    >
                      <Video className="w-4 h-4" />
                      Vimeo
                    </a>
                  )}
                </div>

                {/* Message Button */}
                {user && selectedMember.user_id !== user.user_id && (
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMessageRecipient(selectedMember);
                        setSelectedMember(null);
                      }}
                      className="w-full flex items-center justify-center gap-2 bg-purple hover:bg-purple-hover text-white py-3 px-4 font-medium transition-colors"
                    >
                      <MessageSquare className="w-5 h-5" />
                      Send Message
                    </button>
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Message Dialog */}
        <MessageDialog
          isOpen={!!messageRecipient}
          onClose={() => setMessageRecipient(null)}
          recipient={messageRecipient || {}}
          onSent={() => toast.success("Message sent!")}
        />
      </div>
    </div>
  );
}
